import './SlChipsInput.scss';

import React, { useEffect, useRef, useState } from 'react';

import SlInput from '@shoelace-style/shoelace/dist/react/input';
import SlTag from '@shoelace-style/shoelace/dist/react/tag';
import { translate } from '~l10n/client';

import { toast } from '~shared/components/Toast';

interface ChipsInputProps {
  values: string[];
  setValues: React.Dispatch<React.SetStateAction<string[]>>;
  placeholder: string;
  checkValidValue?: (value: string) => boolean;
  allowDuplicate?: boolean;
  caseSensitive?: boolean;
  validationMessage: string;
}

const ChipsInput = ({
  values,
  setValues,
  placeholder,
  checkValidValue,
  allowDuplicate = false,
  caseSensitive = false,
  validationMessage,
}: ChipsInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef(null);

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const addChips = (chipsToAdd: string[]) => {
    const invalidChips = [];
    for (const chip of chipsToAdd) {
      const valueToAdd = caseSensitive ? chip : chip.toLowerCase();
      if (valueToAdd.trim() !== '') {
        if (checkValidValue && checkValidValue(valueToAdd)) {
          if (!values.includes(valueToAdd)) {
            setValues((prevValues) => [...prevValues, valueToAdd]);
          } else if (allowDuplicate) {
            setValues((prevValues) => [...prevValues, valueToAdd]);
          }
          setInputValue('');
          scrollToBottom();
          inputRef.current?.focus();
        } else {
          invalidChips.push(valueToAdd);
        }
      }
    }

    if (invalidChips.length > 0) {
      toast.critical(validationMessage || translate('Invalid'));
      return;
    }

    setInputValue('');
    scrollToBottom();
    inputRef.current?.focus();
  };

  const handleInputKeyDown = (event: React.KeyboardEvent) => {
    const { key } = event;
    if (['Enter', ' ', ',', ';'].includes(key) && inputValue.trim() !== '') {
      event.preventDefault();
      addChips([inputValue]);
    } else if (
      event.key === 'Backspace' &&
      inputValue === '' &&
      values.length
    ) {
      removeLastChip();
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const paste = event.clipboardData.getData('text');
    const chips = paste.split(/[\s,;]+/).filter((email) => email.trim() !== '');
    addChips(chips);
  };

  const handleInputBlur = () => {
    if (inputValue.trim() !== '') {
      addChips([inputValue]);
    }
  };

  const removeChipByValue = (valueToRemove: string) => {
    setValues((prevValues) =>
      prevValues.filter((value) => value !== valueToRemove)
    );
  };

  const removeLastChip = () => {
    setValues((prevValues) => prevValues.slice(0, prevValues.length - 1));
  };

  const scrollToBottom = () => {
    if (containerRef?.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [values]);

  return (
    <>
      <div
        className="sl-chips-input"
        ref={containerRef}
        onClick={() => inputRef.current?.focus()}
      >
        {values.map((value, index) => (
          <SlTag
            key={index}
            size="medium"
            removable
            onSlRemove={() => removeChipByValue(value)}
          >
            {value}
          </SlTag>
        ))}
        <SlInput
          ref={inputRef}
          placeholder={placeholder}
          value={inputValue}
          onSlInput={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onPaste={handlePaste}
          onSlBlur={handleInputBlur}
          aria-placeholder={placeholder}
          style={{ width: '100%' }}
        ></SlInput>
      </div>
    </>
  );
};

export default ChipsInput;
